<!--
  A Bulma-styled modal dialog.
 
  "title" prop will be displayed in the modal's title bar
  The content of the element (i.e. whatever is between <ModalDialog></ModalDialog>) will be placed in the main body / message area.
  "severity" prop determines overall styling (based on standard Bulma modifiers like "warning" or "info").
  "allowClose" prop controls whether the modal will have an "X" button to close it, and will also be closeable by clicking outside of the modal if true. A "close" event is emitted for this.
  
  Up to three buttons can be shown.
  Pass "firstButtonText" to enable the first button, and "firstButtonSeverity" to determine the styling (based on standard Bulma modifiers).
  Passing corresponding values for "secondButtonText/Severity" or "thirdButtonText/Severity" will enable those buttons.
  You can bind to "firstbuttonselected", "secondbuttonselected" or "thirdbuttonselected" events to be notified when the buttons are clicked.
-->
<template>
  <div class="modal" :class="{ 'is-active': active }">
    <div class="modal-background"></div>
    <div class="modalDialog-content modal-content">
      <article class="message" :class="`is-${severity}`">
        <div class="message-header">
          <p>{{ title }}</p>
          <button class="delete" aria-label="delete" v-if="allowClose" @click="onClose()"></button>
        </div>
        <div class="modalDialog-contentArea message-body">
          <slot />
        </div>
        <footer
          class="modalDialog-buttonArea modal-card-foot"
          :class="{ 'modalDialog-buttonArea-vertical': displayVertically }"
        >
          <button
            v-if="firstButtonText"
            @click="$emit('firstbuttonselected')"
            class="button"
            :class="`is-${firstButtonSeverity}` + (displayVertically ? ' button-vertical' : '')"
          >{{ firstButtonText }}</button>
          <button
            v-if="secondButtonText"
            @click="$emit('secondbuttonselected')"
            class="button"
            :class="`is-${secondButtonSeverity}` + (displayVertically ? ' button-vertical' : '')"
          >{{ secondButtonText }}</button>
          <button
            v-if="thirdButtonText"
            @click="$emit('thirdbuttonselected')"
            class="button"
            :class="`is-${thirdButtonSeverity}` + (displayVertically ? ' button-vertical' : '')"
          >{{ thirdButtonText }}</button>
        </footer>
      </article>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";

const props = defineProps<{
  active: boolean,
  title: string,
  severity?: string,
  firstButtonText?: string,
  firstButtonSeverity?: string,
  secondButtonText?: string,
  secondButtonSeverity?: string,
  thirdButtonText?: string,
  thirdButtonSeverity?: string,
  allowClose?: boolean
}>();

const emit = defineEmits(['close', 'firstbuttonselected', 'secondbuttonselected', 'thirdbuttonselected'])

const onClose = () => {
  emit('close');
};

// If the combined length of the button text is long enough, we'll display them vertically instead of side-by-side
const displayVertically = computed(() => {
  let len = 0;
  if (props.firstButtonText) { len += props.firstButtonText.length; }
  if (props.secondButtonText) { len += props.secondButtonText.length; }
  if (props.thirdButtonText) { len += props.thirdButtonText.length; }
  return len > 40;
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "../assets/variables.scss";

.modalDialog-content {
  padding: 20px;
}

.modalDialog-buttonArea {
  justify-content: right;
}

.modalDialog-contentArea {
  color: #303233 !important;
}

.modalDialog-buttonArea-vertical {
  flex-direction: column;
}

.button-vertical:not(:last-child) {
  margin-bottom: 15px;
}

@media screen and (max-width: $tabletWidth) {
  .modalDialog-buttonArea {
    flex-direction: column;
  }

  .button:not(:last-child) {
    margin-bottom: 15px;
  }
}
</style>
 